import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Box,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { styled } from "@mui/material/styles";

const StyledTable = styled(Table)(({ theme }) => ({
  borderCollapse: "separate",
  borderSpacing: "0 8px",

  "& th": {
    background:
      theme.palette.mode === "dark"
        ? "linear-gradient(180deg, #2b2f3a 0%, #1f2430 100%)"
        : "linear-gradient(180deg, #1a73e8 0%, #1558c0 100%)",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.85rem",
    textAlign: "center",
    position: "sticky",
    top: 0,
    zIndex: 3,
    padding: "12px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
  },

  "& td": {
    backgroundColor: theme.palette.background.paper,
    fontSize: "0.85rem",
    padding: "12px",
    borderBottom: "none",
  },

  "& tbody tr": {
    transition: "all 0.25s ease",
  },

  "& tbody tr:hover td": {
    backgroundColor: theme.palette.mode === "dark" ? "#262b36" : "#f5f9ff",
  },
}));

const AccordionWrapper = styled("div")(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? "linear-gradient(180deg, #1f2430, #181c24)"
      : "linear-gradient(180deg, #f8f9fb, #eef2f7)",
  padding: "14px",
  borderRadius: "10px",
  boxShadow: "inset 0 2px 6px rgba(0,0,0,0.15)",
}));

const CustomTable = ({
  columns,
  rows,
  accordion = false,
  accordionTableColumns = [],
  maxHeight = 500,
  loading = false,
}) => {
  const [openRow, setOpenRow] = useState({});

  const toggleRow = (rowIndex) => {
    setOpenRow((prev) => ({ ...prev, [rowIndex]: !prev[rowIndex] }));
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight,
        overflowY: "auto",
        borderRadius: "14px",
        boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
      }}
    >
      <StyledTable>
        <TableHead>
          <TableRow>
            {accordion && <TableCell />}
            {columns.map((col) => (
              <TableCell key={col.label}>{col.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length + 1} align="center">
                <Box py={3} color="text.secondary">
                  Fetching data...
                </Box>
              </TableCell>
            </TableRow>
          )}

          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length + 1} align="center">
                <Box py={3} color="text.secondary">
                  No data available
                </Box>
              </TableCell>
            </TableRow>
          )}

          {!loading &&
            rows.map((row, rowIndex) => (
              <React.Fragment key={rowIndex}>
                <TableRow>
                  {accordion && (
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => toggleRow(rowIndex)}
                        sx={{
                          transition: "transform 0.25s ease",
                          "&:hover": { transform: "scale(1.15)" },
                        }}
                      >
                        {openRow[rowIndex] ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    </TableCell>
                  )}

                  {columns.map((col) => (
                    <TableCell key={col.field} align="center">
                      {row[col.field]}
                    </TableCell>
                  ))}
                </TableRow>

                {accordion && (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} sx={{ padding: 0 }}>
                      <Collapse
                        in={openRow[rowIndex]}
                        timeout={300}
                        unmountOnExit
                      >
                        <AccordionWrapper>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                {accordionTableColumns.map((col) => (
                                  <TableCell
                                    key={col.label}
                                    align="center"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.8rem",
                                    }}
                                  >
                                    {col.label}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {row.history?.map((hRow, i) => (
                                <TableRow key={i}>
                                  {accordionTableColumns.map((col) => (
                                    <TableCell
                                      key={col.field}
                                      align="center"
                                      sx={{ fontSize: "0.8rem" }}
                                    >
                                      {hRow[col.field]}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </AccordionWrapper>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
        </TableBody>
      </StyledTable>
    </TableContainer>
  );
};

export default CustomTable;
